import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, InputAdornment,
  CircularProgress, Alert, useMediaQuery, useTheme, Divider
} from '@mui/material';
import {
  Phone as PhoneIcon,
  FavoriteOutlined as HeartIcon,
  LocalHospital as HospitalIcon,
  CheckCircleOutline as CheckIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import { authAPI } from '../services/api';

const BLUE = '#00A8E8';
const GREEN = '#86E000';
const DARK_BLUE = '#005B8F';

// ── MSG91 Widget (commented out — replaced with custom OTP) ─────────────────
// const MSG91_WIDGET_ID = '3664726d697a373638363330';
// const MSG91_TOKEN_AUTH = '471864TwJxI2C4YrXk69e3a0d1P1';

const RESEND_SECONDS = 30;

const healthTips = [
  { icon: <HeartIcon sx={{ fontSize: 18 }} />, text: 'Track your vitals anytime' },
  { icon: <HospitalIcon sx={{ fontSize: 18 }} />, text: 'View detailed health reports' },
  { icon: <CheckIcon sx={{ fontSize: 18 }} />, text: 'Download PDF of your results' },
];

export default function PatientLogin({ onLogin }) {
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('mobile'); // 'mobile' | 'otp'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const timerRef = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);

  function startResendTimer() {
    setResendTimer(RESEND_SECONDS);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      setError('Enter a valid 10-digit mobile number');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await authAPI.sendOtp(mobile);
      setStep('otp');
      startResendTimer();
    } catch (err) {
      setError(err.response?.data?.message || 'Mobile number not registered.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError('Enter the 6-digit OTP');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { data: resp } = await authAPI.verifyOtp(mobile, otp);
      const userData = resp.patient
        ? { ...resp.patient, role: 'patient', mobile }
        : { mobile, role: 'patient' };
      localStorage.setItem('token', resp.token);
      localStorage.setItem('patientToken', resp.token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isAuthenticated', 'true');
      if (onLogin) onLogin(userData);
      setRedirecting(true);
      window.location.href = '/portal';
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setError('');
    setLoading(true);
    try {
      await authAPI.sendOtp(mobile);
      setOtp('');
      startResendTimer();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP. Try again.');
    } finally {
      setLoading(false);
    }
  };

  if (redirecting) {
    return (
      <Box sx={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: `linear-gradient(135deg, ${DARK_BLUE} 0%, #00A8E8 60%, #86E000 100%)`,
      }}>
        <CircularProgress size={56} sx={{ color: '#fff', mb: 3 }} />
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
          Loading your health reports...
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
          Please wait a moment
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: '#f0f7fa' }}>

      {/* LEFT PANEL */}
      {!isMobile && (
        <Box sx={{
          flex: 1, position: 'relative', overflow: 'hidden',
          background: `linear-gradient(160deg, #003f66 0%, ${DARK_BLUE} 50%, #007744 100%)`,
        }}>
          <Box sx={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=900&q=80)`,
            backgroundSize: 'cover', backgroundPosition: 'center top',
            opacity: 0.15,
          }} />
          <Box sx={{
            position: 'absolute', inset: 0,
            background: `linear-gradient(160deg, rgba(0,63,102,0.9) 0%, rgba(0,91,143,0.82) 45%, rgba(0,119,68,0.85) 100%)`,
          }} />
          {[
            { size: 300, top: -80, right: -80, opacity: 0.06 },
            { size: 200, bottom: 40, left: -60, opacity: 0.06 },
            { size: 150, top: '40%', left: '30%', opacity: 0.04 },
          ].map((c, i) => (
            <Box key={i} sx={{
              position: 'absolute', borderRadius: '50%',
              width: c.size, height: c.size,
              top: c.top, right: c.right, bottom: c.bottom, left: c.left,
              bgcolor: GREEN, opacity: c.opacity,
            }} />
          ))}
          <Box sx={{
            position: 'relative', zIndex: 1, height: '100%',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            px: 6, py: 8,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6 }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: '14px',
                background: `linear-gradient(135deg, ${GREEN}, ${BLUE})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 8px 24px rgba(134,224,0,0.4)`,
              }}>
                <HeartIcon sx={{ color: '#fff', fontSize: 26 }} />
              </Box>
              <Box>
                <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.3rem', fontFamily: 'Montserrat, sans-serif', lineHeight: 1.1 }}>
                  Eros Wellness
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', letterSpacing: 2, textTransform: 'uppercase' }}>
                  Patient Portal
                </Typography>
              </Box>
            </Box>

            <Typography variant="h3" sx={{
              color: '#fff', fontWeight: 800, lineHeight: 1.2, mb: 2,
              fontFamily: 'Montserrat, sans-serif', fontSize: { md: '2.2rem', lg: '2.5rem' }
            }}>
              Your Health<br />
              <Box component="span" sx={{ color: GREEN }}>Report</Box><br />
              Anytime, Anywhere
            </Typography>

            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem', mb: 5, maxWidth: 360, lineHeight: 1.7 }}>
              Access your complete health screening report securely using your registered mobile number.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 5 }}>
              {healthTips.map((tip, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    color: GREEN, width: 36, height: 36, borderRadius: '10px',
                    bgcolor: 'rgba(134,224,0,0.12)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {tip.icon}
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.88rem' }}>{tip.text}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 3,
              p: 2.5, border: '1px solid rgba(255,255,255,0.1)',
            }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', mb: 1.5 }}>
                Simple 2-Step Access
              </Typography>
              {['Enter your registered mobile number', 'Enter OTP sent to your phone'].map((s, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: i === 0 ? 1 : 0 }}>
                  <Box sx={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                    background: `linear-gradient(135deg, ${BLUE}, ${GREEN})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Typography sx={{ color: '#fff', fontSize: '0.65rem', fontWeight: 700 }}>{i + 1}</Typography>
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem' }}>{s}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* RIGHT PANEL */}
      <Box sx={{
        width: { xs: '100%', md: 480 }, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        p: { xs: 3, sm: 4 }, bgcolor: '#fff',
        boxShadow: '-4px 0 40px rgba(0,0,0,0.06)',
      }}>
        <Box sx={{ width: '100%', maxWidth: 400 }}>

          {isMobile && (
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{
                width: 60, height: 60, borderRadius: '16px', mx: 'auto', mb: 2,
                background: `linear-gradient(135deg, ${GREEN}, ${BLUE})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <HeartIcon sx={{ color: '#fff', fontSize: 28 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: DARK_BLUE, fontFamily: 'Montserrat, sans-serif' }}>
                Eros Wellness
              </Typography>
            </Box>
          )}

          {/* ── STEP 1: Mobile Entry ── */}
          {step === 'mobile' && (
            <>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5, fontFamily: 'Montserrat, sans-serif' }}>
                View Your Report
              </Typography>
              <Typography sx={{ color: '#64748b', mb: 4, fontSize: '0.9rem' }}>
                Enter your registered mobile number to get started
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}

              <form onSubmit={handleSendOtp}>
                <TextField
                  fullWidth label="Mobile Number" value={mobile}
                  onChange={e => { setMobile(e.target.value.replace(/\D/g, '').slice(0, 10)); setError(''); }}
                  required disabled={loading}
                  placeholder="10-digit mobile number"
                  sx={{ mb: 4 }}
                  inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ color: '#4A4A4A', fontWeight: 600, mr: 0.5, fontSize: '0.9rem' }}>+91</Typography>
                        <PhoneIcon sx={{ color: GREEN }} />
                      </InputAdornment>
                    )
                  }}
                />
                <Button
                  type="submit" fullWidth variant="contained" size="large"
                  disabled={loading || mobile.length !== 10}
                  sx={{
                    py: 1.6, fontSize: '1rem', borderRadius: 3, fontWeight: 700,
                    background: `linear-gradient(135deg, ${GREEN} 0%, ${BLUE} 100%)`,
                    color: '#fff',
                    boxShadow: `0 6px 20px rgba(134,224,0,0.3)`,
                    '&:hover': { boxShadow: `0 8px 28px rgba(134,224,0,0.4)`, transform: 'translateY(-1px)' },
                    '&:disabled': { opacity: 0.6 },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
                </Button>
              </form>
            </>
          )}

          {/* ── STEP 2: OTP Verification ── */}
          {step === 'otp' && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '12px',
                  background: `linear-gradient(135deg, ${GREEN}, ${BLUE})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <LockIcon sx={{ color: '#fff', fontSize: 20 }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', fontFamily: 'Montserrat, sans-serif' }}>
                  Enter OTP
                </Typography>
              </Box>
              <Typography sx={{ color: '#64748b', mb: 4, fontSize: '0.9rem' }}>
                OTP sent to <strong>+91 {mobile}</strong>. Valid for 5 minutes.
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}

              <form onSubmit={handleVerifyOtp}>
                <TextField
                  fullWidth label="6-Digit OTP" value={otp}
                  onChange={e => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                  required disabled={loading}
                  placeholder="Enter 6-digit OTP"
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: GREEN }} />
                      </InputAdornment>
                    )
                  }}
                />

                {/* Resend row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Button
                    size="small" variant="text"
                    onClick={() => { setStep('mobile'); setOtp(''); setError(''); clearInterval(timerRef.current); setResendTimer(0); }}
                    sx={{ color: '#64748b', fontSize: '0.8rem', p: 0, textTransform: 'none' }}
                  >
                    ← Change number
                  </Button>
                  <Button
                    size="small" variant="text"
                    onClick={handleResend}
                    disabled={resendTimer > 0 || loading}
                    sx={{ color: resendTimer > 0 ? '#94a3b8' : BLUE, fontSize: '0.8rem', p: 0, textTransform: 'none' }}
                  >
                    {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                  </Button>
                </Box>

                <Button
                  type="submit" fullWidth variant="contained" size="large"
                  disabled={loading || otp.length !== 6}
                  sx={{
                    py: 1.6, fontSize: '1rem', borderRadius: 3, fontWeight: 700,
                    background: `linear-gradient(135deg, ${GREEN} 0%, ${BLUE} 100%)`,
                    color: '#fff',
                    boxShadow: `0 6px 20px rgba(134,224,0,0.3)`,
                    '&:hover': { boxShadow: `0 8px 28px rgba(134,224,0,0.4)`, transform: 'translateY(-1px)' },
                    '&:disabled': { opacity: 0.6 },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Login'}
                </Button>
              </form>
            </>
          )}

          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" sx={{ color: '#94a3b8', px: 1 }}>Admin?</Typography>
          </Divider>

          <Button
            component={Link} to="/login"
            fullWidth variant="outlined" size="large"
            sx={{
              py: 1.5, borderRadius: 3, fontWeight: 600, fontSize: '0.9rem',
              borderColor: BLUE, color: DARK_BLUE,
              '&:hover': { borderColor: BLUE, bgcolor: 'rgba(0,168,232,0.06)' }
            }}
          >
            Admin Portal Login
          </Button>

          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', textAlign: 'center', mt: 4 }}>
            Eros Wellness Platform © {new Date().getFullYear()} Eros Universe
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
